import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Wrench, RefreshCw, CheckCircle2, AlertTriangle, XCircle, CircleOff, Copy, Mail, ExternalLink, ChevronRight } from "lucide-react";

const STATUS = {
  ok: { label: "OK", icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  warn: { label: "Da verificare", icon: AlertTriangle, cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  error: { label: "Errore", icon: XCircle, cls: "text-red-400 bg-red-500/10 border-red-500/30" },
  off: { label: "Non configurato", icon: CircleOff, cls: "text-zinc-400 bg-zinc-800/60 border-zinc-700" },
};
const GROUPS = [["core", "Infrastruttura e dominio"], ["messaging", "Messaggistica"], ["payments", "Pagamenti"], ["backup", "Backup"], ["other", "Altri servizi"]];

function CheckCard({ r }) {
  const s = STATUS[r.status] || STATUS.off;
  return (
    <div className={`rounded-xl border p-3 flex gap-3 ${s.cls}`} data-testid={`maint-check-${r.key}`} data-status={r.status}>
      <s.icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm text-zinc-100 flex items-center gap-2 flex-wrap">{r.label}
            {r.badge && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid={`maint-badge-${r.key}`}>{r.badge}</span>}
          </div>
          <span className="text-[10px] uppercase tracking-wide font-semibold">{s.label}</span>
        </div>
        <p className="text-xs text-zinc-300 mt-0.5 break-words" data-testid={`maint-detail-${r.key}`}>{r.detail}</p>
        {r.fix && <p className="text-xs mt-1.5 text-zinc-200"><b>Rimedio:</b> {r.fix}</p>}
        {r.link && <Link to={r.link} className="inline-flex items-center gap-1 text-[11px] mt-1.5 text-blue-300 hover:underline" data-testid={`maint-link-${r.key}`}>Apri sezione <ChevronRight className="h-3 w-3" /></Link>}
      </div>
    </div>
  );
}

export default function Maintenance() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState("basic");
  const [kinds, setKinds] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [deliv, setDeliv] = useState(null);
  const [sending, setSending] = useState(false);
  const loadDeliv = () => api.get("/maintenance/email-log").then((r) => setDeliv(r.data)).catch(() => {});
  useEffect(() => { api.get("/maintenance/last").then((r) => setData(r.data)).catch(() => {}); api.get("/maintenance/test-email/kinds").then((r) => setKinds(r.data)).catch(() => {}); loadDeliv(); }, []);
  const run = async () => {
    setRunning(true);
    try { const { data } = await api.get("/maintenance/checks"); setData(data); toast.success("Verifica completata"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copiato"); } catch { toast.error("Copia non riuscita"); } };
  const testEmail = async (all = false) => {
    if (!email.trim()) return toast.error("Inserisci un indirizzo");
    setSending(true);
    const list = all ? kinds.map((k) => k.kind) : [kind];
    for (const k of list) {
      try { const { data } = await api.post("/maintenance/test-email", { to: email.trim(), kind: k }); setEmailLog((l) => [{ ...data, ok: true }, ...l]); }
      catch (e) { setEmailLog((l) => [{ kind: k, label: kinds.find((x) => x.kind === k)?.label || k, ok: false, error: formatApiError(e), at: new Date().toISOString() }, ...l]); }
    }
    setSending(false);
    toast.success(all ? `Inviati ${list.length} test: controlla la casella` : "Email di prova inviata: controlla la casella");
    setTimeout(loadDeliv, 1500);
  };
  const EV = { sent: ["inviata", "bg-zinc-800 text-zinc-300"], delivered: ["consegnata", "bg-emerald-900/60 text-emerald-300"], delivery_delayed: ["in ritardo", "bg-amber-900/60 text-amber-300"], bounced: ["respinta", "bg-red-900/60 text-red-300"], failed: ["fallita", "bg-red-900/60 text-red-300"], complained: ["spam", "bg-red-900/60 text-red-300"] };
  const results = data?.results || [];
  const sum = data?.summary;
  return (
    <div className="space-y-5 animate-fade-up" data-testid="maintenance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="pill bg-zinc-200 text-zinc-700"><Wrench className="h-3 w-3" /> MANUTENZIONE</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Verifica collegamenti</h1>
          <p className="text-sm text-zinc-400 mt-1">Controlla in un clic che tutti i servizi esterni siano allineati al dominio attuale.</p>
          {data?.checked_at && <p className="text-xs text-zinc-500 mt-1" data-testid="maint-checked-at">Ultimo controllo: {new Date(data.checked_at).toLocaleString("it-IT")} · {data.base_url}</p>}
        </div>
        <Button onClick={run} disabled={running} data-testid="maint-run" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"><RefreshCw className={`h-4 w-4 mr-1 ${running ? "animate-spin" : ""}`} /> {running ? "Verifico…" : "Ricontrolla tutto"}</Button>
      </div>

      {sum && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="maint-summary">
          {Object.entries(STATUS).map(([k, s]) => <div key={k} className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${s.cls}`}><s.icon className="h-4 w-4" /><span className="text-2xl font-bold">{sum[k] || 0}</span><span className="text-xs">{s.label}</span></div>)}
        </div>
      )}

      {!data?.results && !running && <div className="card-elev p-6 text-sm text-zinc-400" data-testid="maint-empty">Nessun controllo eseguito: premi <b>Ricontrolla tutto</b>.</div>}

      {GROUPS.map(([g, title]) => {
        const rows = results.filter((r) => r.group === g);
        if (!rows.length) return null;
        return (
          <section key={g} className="space-y-2" data-testid={`maint-group-${g}`}>
            <h2 className="font-heading text-lg font-bold">{title}</h2>
            <div className="grid gap-2 md:grid-cols-2">{rows.map((r) => <CheckCard key={r.key} r={r} />)}</div>
          </section>
        );
      })}

      {data?.urls && (
        <section className="card-elev p-4 space-y-3" data-testid="maint-urls">
          <h2 className="font-heading text-lg font-bold">URL da incollare nei pannelli esterni</h2>
          <p className="text-xs text-zinc-400">Già compilati con il dominio attuale. Dopo un cambio di dominio vanno aggiornati nei servizi indicati.</p>
          <div className="space-y-2">
            {data.urls.map((u) => (
              <div key={u.label} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 flex flex-wrap items-center gap-2" data-testid={`maint-url-${u.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-zinc-200">{u.label} <span className="font-normal text-zinc-500">· {u.where}</span></div>
                  <code className="text-[11px] text-amber-200 break-all">{u.url}</code>
                </div>
                <button type="button" onClick={() => copy(u.url)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-zinc-700 hover:bg-zinc-900" data-testid="maint-copy"><Copy className="h-3 w-3" /> Copia</button>
                <a href={u.url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white"><ExternalLink className="h-4 w-4" /></a>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="card-elev p-4 space-y-2" data-testid="maint-test-email">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Mail className="h-4 w-4" /> Email di prova</h2>
        <div className="flex flex-wrap gap-2">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tuo@indirizzo.it" className="bg-zinc-950 border-zinc-800 flex-1 min-w-[220px]" data-testid="maint-test-email-input" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-10 rounded-md bg-zinc-950 border border-zinc-800 px-3 text-sm" data-testid="maint-test-email-kind">
            {(kinds.length ? kinds : [{ kind: "basic", label: "Collegamento" }]).map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
          </select>
          <Button onClick={() => testEmail(false)} disabled={sending} variant="outline" className="border-zinc-700" data-testid="maint-test-email-send">{sending ? "Invio…" : "Invia email di prova"}</Button>
          <Button onClick={() => testEmail(true)} disabled={sending || !kinds.length} variant="outline" className="border-zinc-700" data-testid="maint-test-email-send-all">Invia tutti i template</Button>
        </div>
        <p className="text-xs text-zinc-500">I template sono quelli reali dei flussi (codice di verifica, link reset password, notifica admin, allegato PDF). Per WhatsApp e SMS usa i pulsanti «Invia di prova» nella sezione <Link to="/spoki" className="text-blue-300 hover:underline">Spoki & SMS</Link>.</p>
        {emailLog.length > 0 && (
          <div className="divide-y divide-zinc-800 text-xs" data-testid="maint-test-email-log">
            {emailLog.map((r, i) => (
              <div key={i} className="py-1.5 flex flex-wrap items-center gap-2" data-testid={`maint-test-email-row-${i}`}>
                <span className={`rounded-full px-2 py-0.5 font-semibold ${r.ok ? "bg-emerald-900/60 text-emerald-300" : "bg-red-900/60 text-red-300"}`}>{r.ok ? "inviata" : "errore"}</span>
                <b>{r.label}</b>
                {r.ok ? <span className="text-zinc-400">via {r.provider} · da {r.from}{r.reply_to ? ` · risposte a ${r.reply_to}` : ""} · id {r.message_id}</span> : <span className="text-red-300">{r.error}</span>}
                <span className="text-zinc-500 ml-auto">{(r.at || "").slice(11, 19)} UTC</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card-elev p-4 space-y-2" data-testid="maint-email-delivery">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Mail className="h-4 w-4" /> Consegna email (webhook Resend)</h2>
          <div className="flex items-center gap-2 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${deliv?.webhook?.configured ? "bg-emerald-900/60 text-emerald-300" : "bg-amber-900/60 text-amber-300"}`} data-testid="maint-webhook-status">{deliv?.webhook?.configured ? "webhook attivo" : "webhook non configurato"}</span>
            {deliv?.webhook?.last_event_at && <span className="text-zinc-500">ultimo evento {deliv.webhook.last_event_type} · {deliv.webhook.last_event_at.slice(0, 16).replace("T", " ")} UTC</span>}
            <Button size="sm" variant="ghost" onClick={loadDeliv} data-testid="maint-email-delivery-refresh">Aggiorna</Button>
          </div>
        </div>
        <p className="text-xs text-zinc-500">Endpoint: <code>{window.location.origin}{deliv?.webhook?.path || "/api/resend/webhook"}</code> · eventi <code>email.delivered</code>, <code>email.bounced</code>, <code>email.opened</code> (apertura solo con tracking attivo sul dominio in Resend). Firma Svix verificata, eventi duplicati ignorati.</p>
        {deliv && deliv.items.length === 0 && <p className="text-xs text-zinc-500">Nessuna email registrata (il registro parte dagli invii Resend effettuati da ora).</p>}
        {deliv?.items?.length > 0 && (
          <div className="divide-y divide-zinc-800 text-xs" data-testid="maint-email-delivery-list">
            {deliv.items.map((r) => (
              <div key={r.email_id} className="py-1.5 flex flex-wrap items-center gap-2" data-testid={`maint-email-delivery-row-${r.email_id}`}>
                <span className={`rounded-full px-2 py-0.5 font-semibold ${(EV[r.last_event] || EV.sent)[1]}`}>{(EV[r.last_event] || EV.sent)[0]}</span>
                {r.engagement && <span className="rounded-full px-2 py-0.5 bg-sky-900/60 text-sky-300">{r.engagement === "opened" ? "aperta" : "click"}</span>}
                <span className="text-zinc-300">{r.to}</span>
                <span className="text-zinc-500 truncate max-w-[40%]">{r.subject}</span>
                {r.bounce?.message && <span className="text-red-300 truncate max-w-[40%]" title={r.bounce.message}>{r.bounce.type}: {r.bounce.message}</span>}
                <span className="text-zinc-500 ml-auto">{(r.sent_at || "").slice(0, 16).replace("T", " ")} UTC</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
